<!--
  Announcement details tile (redesign).

  Renders two stacked cards, mirroring the design:
    1. summary  - branding media, meta line, title, location, company, how to
                  apply and the primary action;
    2. content  - the full announcement text followed by the vacancy facts,
                  the employer requirements and the repeated primary action.

  The host page projects the customer branding cover and logo into the summary
  card through the `annCover` / `annLogo` slots, so pages that have no branding
  (the jobseeker invitation page) simply project nothing.
-->
<article
  class="tile"
  [ngClass]="{
    'tile--public': isPublicView,
    'tile--standalone': !isPublicView
  }"
>
  <section class="card card--summary">
    <div class="card__media"><ng-content select="[annCover]"></ng-content></div>

    <div
      class="card__body summary"
      [class.summary--with-logo]="announcementDetails?.hasLogo"
    >
      @if (isPublicView) {
      <!-- Favourite + share pinned to the top-right corner (HR-3762). -->
      <div class="actions">
        <div class="actions__share">
          <button
            type="button"
            class="actions__btn"
            (click)="onToggleShare()"
            [attr.aria-label]="'label.ShareAnnouncement' | translate"
          >
            <svg-icon
              [src]="
                !isShareButtonClicked
                  ? '/assets/icons/share-' + providerWebsiteId + '.svg'
                  : '/assets/icons/share-' + providerWebsiteId + '-filled.svg'
              "
              class="actions__icon-wrapper"
              [svgClass]="'actions__icon'"
            ></svg-icon>
          </button>

          @if (isShareButtonClicked) {
          <div class="share-menu" @listAnimation>
            <div class="share-menu__item" (click)="onCopyLink()">
              <svg-icon src="assets/icons/share-copy-link.svg"></svg-icon>
              @if (isAnnouncementLinkCopied) {
              <span [@copiedAnimation] class="share-menu__copied">
                {{ 'label.LinkCopied' | translate }}
              </span>
              }
            </div>
            <div
              class="share-menu__item"
              (click)="onSocialShare(socialMediaShareLinks.facebookShareUrl)"
            >
              <svg-icon src="assets/icons/share-facebook.svg"></svg-icon>
            </div>
            <div
              class="share-menu__item"
              (click)="onSocialShare(socialMediaShareLinks.linkedInShareUrl)"
            >
              <svg-icon src="assets/icons/share-linkedin.svg"></svg-icon>
            </div>
            <div
              class="share-menu__item"
              (click)="onSocialShare(socialMediaShareLinks.xShareUrl)"
            >
              <svg-icon src="assets/icons/share-x.svg"></svg-icon>
            </div>
            <div
              class="share-menu__item"
              (click)="onSocialShare(socialMediaShareLinks.mailShareUrl)"
            >
              <svg-icon src="assets/icons/share-gmail.svg"></svg-icon>
            </div>
          </div>
          }
        </div>

        <button
          type="button"
          class="actions__btn"
          (click)="onToggleFavorite()"
          [attr.aria-label]="
            (isFavorite ? 'label.RemoveFromFavorites' : 'label.AddToFavorites')
              | translate
          "
        >
          <svg-icon
            [src]="
              isFavorite
                ? '/assets/icons/fav-icon-filled-' + providerWebsiteId + '.svg'
                : '/assets/icons/fav-icon-' + providerWebsiteId + '.svg'
            "
            class="actions__icon-wrapper"
            [svgClass]="'actions__icon'"
          ></svg-icon>
        </button>
      </div>
      }

      <!--
        From $medium-size up the summary text and the logo plate share one row,
        with the plate's bottom level with the company name. Below it both
        wrappers dissolve into the card's column (see the stylesheet).
      -->
      <div class="summary__intro">
        <div class="summary__text">
          <div class="meta">
            @if (listingSectionNameMap[announcementDetails?.listingSection]) {
            <span class="meta__section">
              {{
                listingSectionNameMap[announcementDetails?.listingSection]
                  | translate
              }}
            </span>
            }

            <span class="meta__dates">
              <span
                [class.meta__date--today]="announcementDetails.isPublishedToday"
                >{{
                  announcementDetails.isPublishedToday
                    ? ('label.Today' | translate)
                    : (publishDate | date: 'dd MMM':timeZone:currentLanguage)
                }}</span
              >
              -
              <span
                [class.meta__date--today]="announcementDetails.isDeadLineToday"
                >{{
                  announcementDetails.isDeadLineToday
                    ? ('label.Today' | translate)
                    : (announcementDetails?.deadlineDate
                      | date: 'dd MMM':timeZone:currentLanguage)
                }}</span
              >
            </span>

            @if (isEditedVisible) {
            <span class="meta__edited">
              {{ 'label.Edited' | translate }} -
              <span
                [class.meta__date--today]="announcementDetails.isEditedToday"
                >{{
                  announcementDetails.isEditedToday
                    ? ('label.Today' | translate)
                    : (announcementDetails?.moderationLastChangeDate
                      | date: 'dd MMM':timeZone:currentLanguage)
                }}</span
              >
            </span>
            }
          </div>

          <h1 class="summary__title">
            <span>{{ announcementDetails?.title }}</span>
            @if (announcementDetails?.isWorkFromHome) {
            <svg-icon
              title="{{ 'label.WorkFromHome' | translate }}"
              src="/assets/icons/home-green.svg"
              class="summary__title-icon"
              [svgClass]="'home-icon'"
            ></svg-icon>
            }
          </h1>

          <div class="summary__location">
            <hrra-announcement-location
              [locations]="announcementDetails?.extendedLocations"
              [redesign]="true"
            ></hrra-announcement-location>
          </div>

          <div class="summary__company">
            @if (announcementDetails?.isAnonymous) {
            {{ announcementDetails?.customerName }}
            } @else {
            <a
              [routerLink]="[
                '/customer',
                announcementDetails?.customerId,
                announcementDetails?.urlFriendlyCustomerName
              ]"
              class="summary__company-link"
            >
              {{ announcementDetails?.customerName }}
            </a>
            }
          </div>
        </div>

        <!--
        Branding row. Below $medium-size the design drops it to the bottom of
        the summary card, with the listing section label on its left, so the
        two live in one wrapper the mobile rules turn into a grid row. From
        $medium-size up only the logo shows, beside the summary text.
      -->
        <div class="summary__brand">
          @if (listingSectionNameMap[announcementDetails?.listingSection]) {
          <span class="summary__brand-section">
            {{
              listingSectionNameMap[announcementDetails?.listingSection]
                | translate
            }}
          </span>
          }

          <div class="summary__logo">
            <ng-content select="[annLogo]"></ng-content>
          </div>
        </div>
      </div>

      @if ( announcementDetails?.applicationMethod ===
      applicationMethod.EmailPhoneAddress ) {
      <div class="contacts">
        <div class="contacts__title">
          {{ 'label.ApplicationInformation' | translate }}
        </div>
        @if (announcementDetails?.applicationDetails.email) {
        <div class="contacts__row">
          <span class="contacts__label">{{ 'label.Email' | translate }}:</span>
          <span class="contacts__value">{{
            announcementDetails?.applicationDetails.email
          }}</span>
        </div>
        } @if (announcementDetails?.applicationDetails.phoneNumber) {
        <div class="contacts__row">
          <span class="contacts__label">{{ 'label.Phone' | translate }}:</span>
          <span class="contacts__value">{{
            announcementDetails?.applicationDetails.phoneNumber
          }}</span>
        </div>
        } @if (announcementDetails?.applicationDetails.applicationAddress) {
        <div class="contacts__row">
          <span class="contacts__label"
            >{{ 'label.Address' | translate }}:</span
          >
          <span class="contacts__value">{{
            announcementDetails?.applicationDetails.applicationAddress
          }}</span>
        </div>
        }
      </div>
      } @if (announcementDetails?.hasAttachment) {
      <div class="attachment">
        <a class="attachment__link" [href]="announcementDetails.attachmentUrl">
          <svg-icon
            class="attachment__icon"
            src="assets/icons/attachment-orange.svg"
            [svgClass]="'attachment-icon'"
          ></svg-icon>
          <span>{{ 'label.Attachment' | translate }}</span>
        </a>
      </div>
      }
    </div>
  </section>

  <section class="card card--content">
    <div class="card__body">
      <!--
        Primary action, centred above the announcement text. The summary card
        carries no apply actions of its own, so this is their first place.
      -->
      <div class="card__header">
        <ng-container *ngTemplateOutlet="applyActions"></ng-container>
      </div>

      <h2 class="section__title">
        {{ 'label.FullAnnouncementText' | translate }}
      </h2>

      <div
        class="description"
        [innerHTML]="announcementDetails?.description | safeHtml"
      ></div>

      <section class="section">
        <h3 class="section__title section__title--accent">
          {{ 'label.InformationAboutVacantPosition' | translate }}:
        </h3>

        <dl class="facts">
          @if ( announcementDetails?.announcementRequirements.specializationList
          .length ) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{ 'label.SpecializationPosition' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( specialization of announcementDetails
              ?.announcementRequirements.specializationList; track $index ) {
              <span class="taxonomy">
                <span class="taxonomy__parent">{{ specialization.name }}</span>
                <svg-icon
                  class="taxonomy__icon"
                  src="/assets/icons/chevron-right-blue.svg"
                ></svg-icon>
                @for ( child of specialization.children; track $index; let last
                = $last ) {
                <span class="taxonomy__child">{{
                  last ? child.name : child.name + ','
                }}</span>
                }
              </span>
              }
            </dd>
          </div>
          } @if
          (announcementDetails?.announcementRequirements.industryList.length) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{ 'label.IndustryField' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( industry of announcementDetails?.announcementRequirements
              .industryList; track $index ) {
              <span class="taxonomy">
                <span class="taxonomy__parent">{{ industry.name }}</span>
                <svg-icon
                  class="taxonomy__icon"
                  src="/assets/icons/chevron-right-blue.svg"
                ></svg-icon>
                @for ( child of industry.children; track $index; let last =
                $last ) {
                <span class="taxonomy__child">{{
                  last ? child.name : child.name + ','
                }}</span>
                }
              </span>
              }
            </dd>
          </div>
          } @if (
          announcementDetails?.announcementRequirements.seniorityLevels.length )
          {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.SeniorityLevel' | translate }}:
            </dt>
            <dd class="facts__value">
              {{
                announcementDetails?.announcementRequirements.seniorityLevels
              }}
            </dd>
          </div>
          } @if
          (announcementDetails?.announcementRequirements?.workScheduleName) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.ScheduleType' | translate }}:
            </dt>
            <dd class="facts__value">
              {{
                announcementDetails?.announcementRequirements?.workScheduleName
              }}
            </dd>
          </div>
          } @if
          (announcementDetails?.announcementRequirements?.employmentTypeName) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.EmploymentForm' | translate }}:
            </dt>
            <dd class="facts__value">
              {{
                announcementDetails?.announcementRequirements
                  ?.employmentTypeName
              }}
            </dd>
          </div>
          } @if (
          announcementDetails?.announcementRequirements?.travelRequirements
          ?.isTravelRequired ) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.BusinessTrip' | translate }}:
            </dt>
            <dd class="facts__value">
              {{
                announcementDetails?.announcementRequirements
                  ?.travelRequirements?.isTravelRequired
                  ? ('label.Yes' | translate)
                  : ('label.No' | translate)
              }}{{
                announcementDetails?.announcementRequirements
                  ?.travelRequirements.travelPercentage
                  ? '(' +
                    announcementDetails?.announcementRequirements
                      ?.travelRequirements.travelPercentage +
                    '%) '
                  : ''
              }}{{
                announcementDetails?.announcementRequirements
                  ?.travelRequirements.isTravelAbroad &&
                announcementDetails?.announcementRequirements
                  ?.travelRequirements.isTravelDomestic
                  ? '(' +
                    ('label.Domestic' | translate) +
                    '+' +
                    ('label.Abroad' | translate) +
                    ')'
                  : announcementDetails?.announcementRequirements
                      ?.travelRequirements.isTravelAbroad &&
                    !announcementDetails?.announcementRequirements
                      ?.travelRequirements.isTravelDomestic
                  ? '(' + ('label.Abroad' | translate) + ')'
                  : !announcementDetails?.announcementRequirements
                      ?.travelRequirements.isTravelAbroad &&
                    announcementDetails?.announcementRequirements
                      ?.travelRequirements.isTravelDomestic
                  ? '(' + ('label.Domestic' | translate) + ')'
                  : ''
              }}
            </dd>
          </div>
          } @if ( announcementDetails?.type === announcementType.Vacancy ||
          announcementDetails?.type === announcementType.Other ) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.WorkLocation' | translate }}:
            </dt>
            <dd class="facts__value">
              {{ announcementDetails?.employmentFormTypeName }}
            </dd>
          </div>
          } @if (announcementDetails?.businessTravel) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.BusinessTrip' | translate }}:
            </dt>
            <dd class="facts__value">
              {{ 'label.BusinessTravel' | translate }}
            </dd>
          </div>
          } @if ( (announcementDetails?.salaryFrom !== null ||
          announcementDetails?.salaryTo !== null ||
          announcementDetails?.bonusFrom !== null ||
          announcementDetails?.bonusTo !== null ||
          announcementDetails?.isWithBonus !== false) &&
          (!announcementDetails?.hideSalary || announcementDetails?.hideSalary
          === null) ) {
          <div class="facts__row">
            <dt class="facts__label">{{ 'label.Salary' | translate }}:</dt>
            <dd class="facts__value">
              @if ( announcementDetails?.salaryFrom ||
              announcementDetails?.salaryTo ) {
              <span class="nowrap">
                <span>{{ 'label.Fixed' | translate }} </span>
                <span>{{ announcementDetails?.salaryFrom }}</span>
                @if (announcementDetails?.salaryTo != null) {
                <span> - {{ announcementDetails?.salaryTo }}</span>
                }
              </span>
              }
              <span class="nowrap">
                @if ( announcementDetails?.bonusFrom == null &&
                announcementDetails?.bonusTo == null &&
                announcementDetails?.isWithBonus ) {
                <span> (+ {{ 'label.Bonus' | translate }}) </span>
                } @if ( (announcementDetails?.bonusFrom ||
                announcementDetails?.bonusTo) &&
                announcementDetails?.isWithBonus ) {
                <span> (+ {{ 'label.Bonus' | translate }} </span>
                } @if ( announcementDetails?.bonusFrom &&
                announcementDetails?.isWithBonus ) {
                <span> {{ announcementDetails?.bonusFrom }} </span>
                } @if ( announcementDetails?.bonusFrom !== null &&
                announcementDetails?.bonusTo !== null &&
                announcementDetails?.isWithBonus ) {
                <span> - </span>
                } @if ( announcementDetails?.bonusTo &&
                announcementDetails?.isWithBonus ) {
                <span> {{ announcementDetails?.bonusTo }} </span>
                } @if ( (announcementDetails?.bonusFrom ||
                announcementDetails?.bonusTo) &&
                announcementDetails?.isWithBonus ) {
                <span>) </span>
                }
              </span>
            </dd>
          </div>
          } @if (announcementDetails?.benefits?.length > 0) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.AdditionalBenefits' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( benefit of announcementDetails?.benefits; track $index; let
              last = $last ) {
              <span>{{ last ? benefit : benefit + ',' }}</span>
              }
            </dd>
          </div>
          }
        </dl>
      </section>

      @if (announcementDetails?.type === announcementType.Vacancy) {
      <section class="section">
        <h3 class="section__title section__title--accent">
          {{ 'label.EmployerRequirement' | translate }}:
        </h3>

        <dl class="facts">
          @if (announcementDetails?.workExperienceType !== null) {
          <div class="facts__row">
            <dt class="facts__label">
              {{
                announcementDetails?.workExperienceType ===
                  workExperienceTypeEnum.WithExperience ||
                announcementDetails?.workExperienceType ===
                  workExperienceTypeEnum.WithoutExperience
                  ? ('label.WorkExperience' | translate)
                  : announcementDetails?.employerRequirements
                      .isWorkExperienceYearsMandatory
                  ? ('label.MandatoryExperience' | translate)
                  : ('label.DesiredExperience' | translate)
              }}:
            </dt>
            <dd class="facts__value">
              <span>{{
                announcementWorkExperienceEnumMap[
                  announcementDetails.workExperienceType
                ] | translate
              }}</span>
              @if ( announcementDetails?.workExperienceFrom !== null &&
              announcementDetails?.workExperienceTo !== null ) {
              <span>
                {{
                  'text.ExperienceYears'
                    | translate
                      : {
                          count: announcementDetails?.workExperienceTo,
                          fromyears: announcementDetails?.workExperienceFrom,
                          toyears: announcementDetails?.workExperienceTo
                        }
                }}
              </span>
              } @if ( announcementDetails?.workExperienceFrom !== null &&
              announcementDetails?.workExperienceTo === null ) {
              <span>
                {{ 'label.ExperienceYears' | translate }}
                {{
                  'text.ExperienceFromYears'
                    | translate
                      : {
                          count: announcementDetails?.workExperienceFrom,
                          fromyears: announcementDetails?.workExperienceFrom
                        }
                }}
              </span>
              } @if ( announcementDetails?.workExperienceTo !== null &&
              announcementDetails?.workExperienceFrom === null ) {
              <span>
                {{ 'label.ExperienceYears' | translate }}
                {{
                  'text.ExperienceToYears'
                    | translate
                      : {
                          count: announcementDetails?.workExperienceTo,
                          toyears: announcementDetails?.workExperienceTo
                        }
                }}
              </span>
              }
            </dd>
          </div>
          } @if ( announcementDetails?.workExperienceType ===
          workExperienceTypeEnum.Mandatory &&
          !announcementDetails?.employerRequirements .isSpecializationIgnored &&
          announcementDetails?.employerRequirements.specializationList.length )
          {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{
                ('label.SpecializationPosition' | translate) +
                  '(' +
                  ((announcementDetails?.employerRequirements
                    .isSpecializationMandatory
                    ? 'label.Mandatory'
                    : 'label.Desired'
                  ) | translate) +
                  ')'
              }}:
            </dt>
            <dd class="facts__value">
              @for ( specialization of announcementDetails?.employerRequirements
              .specializationList; track $index ) {
              <span class="taxonomy">
                <span class="taxonomy__parent">{{ specialization.name }}</span>
                <svg-icon
                  class="taxonomy__icon"
                  src="/assets/icons/chevron-right-blue.svg"
                ></svg-icon>
                @for ( child of specialization.children; track $index; let last
                = $last ) {
                <span class="taxonomy__child">{{
                  last ? child.name : child.name + ','
                }}</span>
                }
              </span>
              }
            </dd>
          </div>
          } @if ( announcementDetails?.employerRequirements.industryList.length
          && announcementDetails?.workExperienceType ===
          workExperienceTypeEnum.Mandatory &&
          !announcementDetails?.employerRequirements.isIndustryIgnored ) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{
                ('label.IndustryField' | translate) +
                  '(' +
                  ((announcementDetails?.employerRequirements
                    .isIndustryMandatory
                    ? 'label.Mandatory'
                    : 'label.Desired'
                  ) | translate) +
                  ')'
              }}:
            </dt>
            <dd class="facts__value">
              @for ( industry of announcementDetails?.employerRequirements
              .industryList; track $index ) {
              <span class="taxonomy">
                <span class="taxonomy__parent">{{ industry.name }}</span>
                <svg-icon
                  class="taxonomy__icon"
                  src="/assets/icons/chevron-right-blue.svg"
                ></svg-icon>
                @for ( child of industry.children; track $index; let last =
                $last ) {
                <span class="taxonomy__child">{{
                  last ? child.name : child.name + ','
                }}</span>
                }
              </span>
              }
            </dd>
          </div>
          } @if (
          announcementDetails?.employerRequirements.seniorityLevels.length &&
          announcementDetails?.workExperienceType ===
          workExperienceTypeEnum.Mandatory ) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.SeniorityLevel' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( seniorityLevel of announcementDetails?.employerRequirements
              .seniorityLevels; track $index; let last = $last ) {
              <span>{{ last ? seniorityLevel : seniorityLevel + ',' }}</span>
              }
            </dd>
          </div>
          } @if
          (announcementDetails?.employerRequirements.educationLevels.length) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{ 'label.AnnouncementEducationLevel' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( educationLevel of announcementDetails?.employerRequirements
              .educationLevels; track $index; let last = $last ) {
              <span>{{ last ? educationLevel : educationLevel + ', ' }}</span>
              }
            </dd>
          </div>
          } @if (
          announcementDetails?.employerRequirements.educationPrograms.length ) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">
              {{ 'label.EducationPrograms' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( educationProgram of
              announcementDetails?.employerRequirements .educationPrograms;
              track $index; let last = $last ) {
              <span>{{
                last ? educationProgram : educationProgram + ', '
              }}</span>
              }
            </dd>
          </div>
          } @if (announcementDetails?.isSuitableForStudent) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.SuitableForStudents' | translate }}:
            </dt>
            <dd class="facts__value">{{ 'label.Yes' | translate }}</dd>
          </div>
          } @if (announcementDetails?.languages.length > 0) {
          <div class="facts__row">
            <dt class="facts__label">{{ 'label.Languages' | translate }}:</dt>
            <dd class="facts__value">
              @for ( language of announcementDetails?.languages; track $index;
              let last = $last ) {
              <span>{{ language }}{{ last ? '' : ',' }}</span>
              }
            </dd>
          </div>
          }

          <!-- technical (hard) skills -->
          @if (announcementDetails?.employerRequirements?.hardSkills?.length) {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">{{ 'label.HardSkills' | translate }}:</dt>
            <dd class="facts__value">
              @for ( skill of announcementDetails?.employerRequirements
              ?.hardSkills; track $index; let last = $last ) {
              <span>{{
                (skill.name ? skill.name : skill.userProvidedText) +
                  (last ? '' : '; ')
              }}</span>
              }
            </dd>
          </div>
          } @if (announcementDetails?.employerRequirements?.softSkills?.length)
          {
          <div class="facts__row facts__row--stacked">
            <dt class="facts__label">{{ 'label.SoftSkills' | translate }}:</dt>
            <dd class="facts__value">
              @for ( skill of announcementDetails?.employerRequirements
              ?.softSkills; track $index; let last = $last ) {
              <span>{{
                (skill.name ? skill.name : skill.userProvidedText) +
                  (last ? '' : '; ')
              }}</span>
              }
            </dd>
          </div>
          } @if (announcementDetails?.drivingLicenses.length > 0) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.DrivingLicence' | translate }}:
            </dt>
            <dd class="facts__value">
              @for ( drivingLicense of announcementDetails?.drivingLicenses;
              track $index; let last = $last ) {
              <span
                >{{ drivingLicense + ' ' + ('label.Category' | translate) }}
                {{ last ? '' : ',' }}</span
              >
              }
            </dd>
          </div>
          } @if ( announcementDetails?.drivingLicenses.length > 0 &&
          announcementDetails?.employerRequirements.hasTransport &&
          announcementDetails?.employerRequirements.transportTypes ) {
          <div class="facts__row">
            <dt class="facts__label">
              {{ 'label.OwnTransport' | translate }}:
            </dt>
            <dd class="facts__value">
              <span>{{ 'label.Yes' | translate }}</span>
              @for ( transport of announcementDetails?.employerRequirements
              .transportTypes; track $index; let last = $last; let first =
              $first ) {
              <span>
                {{
                  first && last
                    ? '(' + transport + ')'
                    : last
                    ? transport + ')'
                    : first
                    ? '(' + transport + ','
                    : transport + ','
                }}
              </span>
              }
            </dd>
          </div>
          }
        </dl>
      </section>
      }

      <!-- Repeated primary action, closing the announcement text. -->
      <div class="card__footer">
        <ng-container *ngTemplateOutlet="applyActions"></ng-container>
      </div>
    </div>
  </section>
</article>

<!--
  Primary apply actions of the announcement text card. Rendered twice: once
  above the text and once closing it.
-->
<ng-template #applyActions>
  <!-- External apply: always visible for a redirecting announcement. -->
  @if ( isPublicView && currentUserType !== userType.Internal &&
  announcementDetails?.applicationMethod === applicationMethod.RedirectToUrl ) {
  <a
    [ngClass]="{
      'apply-btn--blocked':
        isSignedIn && user?.userType === userType.customerAgent
    }"
    (click)="onExternalApply()"
    class="apply-btn apply-btn--external"
    target="_blank"
  >
    <svg-icon src="/assets/icons/send-cv-external.svg"></svg-icon>
    <!-- One line, "Apply (on another website)", as in the design. -->
    <span class="apply-btn__label">
      {{ 'label.Apply' | translate }} {{ 'label.ExternalApply' | translate }}
    </span>
  </a>
  } @if (isBrowser && !isJobseekerInvited) { @if (currentUserType !==
  userType.Internal) { @if ( !showInviteResponse &&
  announcementDetails?.applicationMethod == applicationMethod.ViaWebsite &&
  ((isJobseekerSignedIn && !announcementDetails?.isBeenApplied) ||
  !isJobseekerSignedIn) ) {
  <button
    type="button"
    [ngClass]="{
      'apply-btn--blocked':
        isSignedIn && user?.userType == userType.customerAgent
    }"
    class="apply-btn"
    (click)="onCvSend()"
  >
    <svg-icon src="/assets/icons/send-cv-ats.svg"></svg-icon>
    <span class="apply-btn__label">{{ 'label.EasyApply' | translate }}</span>
  </button>
  } @if ( !showInviteResponse && isJobseekerSignedIn &&
  announcementDetails?.isBeenApplied ) {
  <div class="apply-btn apply-btn--sent">
    {{ 'label.CvSent' | translate }}
  </div>
  } } } @else if (isBrowser && isJobseekerInvited) {
  <div class="invite">
    <div class="invite__text">
      {{
        (isInviteEditMode
          ? 'label.ChangeStatus'
          : isInviteStatusChosen
          ? 'label.ChosenStatus'
          : 'label.YouAreInvitedToThisVacancy'
        ) | translate
      }}
    </div>

    @if (isInviteStatusChosen) {
    <div class="invite__chosen">
      <div
        class="invite__btn invite__btn--chosen"
        [ngClass]="
          'invite__btn--chosen-' +
          inviteResponseModifierMap[announcementInvitedJobseeker.responseStatus]
        "
      >
        {{
          inviteResponseLabelMap[announcementInvitedJobseeker.responseStatus]
            | translate
        }}
      </div>
      @if (canEditInviteResponse) {
      <button type="button" class="invite__edit" (click)="onEnableInviteEdit()">
        <svg-icon
          class="invite__edit-icon"
          src="/assets/icons/edit-pencil.svg"
        ></svg-icon>
        <span class="invite__edit-text">{{
          'label.YouCanChangeInvitationStatusOnce' | translate
        }}</span>
      </button>
      }
    </div>
    } @else { @if ( announcementDetails?.applicationMethod ==
    applicationMethod.ViaWebsite && ((isJobseekerSignedIn &&
    !announcementDetails?.isBeenApplied) || !isJobseekerSignedIn) ) {
    <button
      type="button"
      class="invite__btn invite__btn--interested-and-apply"
      [class.invite__btn--selected]="
        announcementInvitedJobseeker.responseStatus ===
        inviteResponse.InterestedAndApplied
      "
      [disabled]="isInviteOptionDisabled(inviteResponse.InterestedAndApplied)"
      (click)="onInviteRespond(inviteResponse.InterestedAndApplied)"
    >
      {{ 'label.InvitationStatusInterestedAndApply' | translate }}
    </button>
    }
    <button
      type="button"
      class="invite__btn invite__btn--interested"
      [class.invite__btn--selected]="
        announcementInvitedJobseeker.responseStatus ===
        inviteResponse.Interested
      "
      [disabled]="isInviteOptionDisabled(inviteResponse.Interested)"
      (click)="onInviteRespond(inviteResponse.Interested)"
    >
      {{ 'label.InvitationStatusInterested' | translate }}
    </button>
    <button
      type="button"
      class="invite__btn invite__btn--not-interested"
      [class.invite__btn--selected]="
        announcementInvitedJobseeker.responseStatus ===
        inviteResponse.NotInterested
      "
      [disabled]="isInviteOptionDisabled(inviteResponse.NotInterested)"
      (click)="onInviteRespond(inviteResponse.NotInterested)"
    >
      {{ 'label.InvitationStatusNotInterested' | translate }}
    </button>
    }
  </div>
  }
</ng-template>
